import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import usePermissions from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../config/permissions';

const TABS = ['Trainings', 'Enrollments', 'MCPD Records'];

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  upcoming: 'bg-blue-100 text-blue-700',
  ongoing: 'bg-green-100 text-green-700',
  completed: 'bg-purple-100 text-purple-700',
  cancelled: 'bg-red-100 text-red-700',
};

const LEVEL_COLORS = {
  beginner: 'bg-emerald-100 text-emerald-700',
  intermediate: 'bg-amber-100 text-amber-700',
  advanced: 'bg-red-100 text-red-700',
};

const CATEGORY_OPTIONS = [
  { value: 'surveying', label: 'Surveying & Geomatics' },
  { value: 'gis', label: 'GIS & Remote Sensing' },
  { value: 'law', label: 'Land Law & Policy' },
  { value: 'ethics', label: 'Ethics & Standards' },
  { value: 'technology', label: 'Technology & Innovation' },
  { value: 'field', label: 'Field Practical' },
  { value: 'management', label: 'Project Management' },
  { value: 'other', label: 'Other' },
];

const LEVEL_OPTIONS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

function FormInput({ label, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
      <input {...props} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
    </div>
  );
}

function FormSelect({ label, options, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
      <select {...props} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

export default function AdminMCPD() {
  const [tab, setTab] = useState('Trainings');
  const [stats, setStats] = useState(null);

  const fetchStats = useCallback(() => {
    api.get('/admin/mcpd/trainings/stats/').then(r => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">MCPD Training Platform</h1>
      <p className="text-sm text-gray-500 mb-6">Manage bootcamp trainings, enrollments, and CPD records</p>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total Trainings" value={stats.total} color="text-gray-900" />
          <StatCard label="Upcoming" value={stats.upcoming} color="text-blue-600" />
          <StatCard label="Ongoing" value={stats.ongoing} color="text-green-600" />
          <StatCard label="Total Enrolled" value={stats.total_enrollments} color="text-purple-600" />
        </div>
      )}

      <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-fit">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition ${
              tab === t ? 'bg-white text-[#0B5D1E] shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>{t}</button>
        ))}
      </div>

      {tab === 'Trainings' && <TrainingsTab onRefreshStats={fetchStats} />}
      {tab === 'Enrollments' && <EnrollmentsTab />}
      {tab === 'MCPD Records' && <MCPDRecordsTab />}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border p-4">
      <p className="text-xs text-gray-500 font-semibold uppercase">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// ═══ TRAININGS TAB ═══
// ══════════════════════════════════════════════════════════════

function TrainingsTab({ onRefreshStats }) {
  const { canEdit, canAdd, canDelete } = usePermissions();
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editTraining, setEditTraining] = useState(null);
  const [viewTraining, setViewTraining] = useState(null);

  const fetchTrainings = useCallback(() => {
    setLoading(true);
    const params = statusFilter ? `?status=${statusFilter}` : '';
    api.get(`/admin/mcpd/trainings/${params}`)
      .then(r => setTrainings(r.data.results || r.data))
      .catch(() => toast.error('Failed to load trainings.'))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { fetchTrainings(); }, [fetchTrainings]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this training? All modules, resources, and enrollments will be removed.')) return;
    try {
      await api.delete(`/admin/mcpd/trainings/${id}/`);
      toast.success('Training deleted.');
      fetchTrainings();
      onRefreshStats();
    } catch { toast.error('Failed to delete.'); }
  };

  const handleBack = () => {
    setViewTraining(null);
    setShowForm(false);
    setEditTraining(null);
    fetchTrainings();
    onRefreshStats();
  };

  if (viewTraining) return <TrainingDetailView training={viewTraining} onBack={handleBack} />;
  if (showForm) return <TrainingForm training={editTraining} onBack={handleBack} />;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setStatusFilter('')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${!statusFilter ? 'bg-[#0B5D1E] text-white' : 'bg-white border text-gray-600 hover:bg-gray-50'}`}>All</button>
          {STATUS_OPTIONS.map(s => (
            <button key={s.value} onClick={() => setStatusFilter(s.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${statusFilter === s.value ? 'bg-[#0B5D1E] text-white' : 'bg-white border text-gray-600 hover:bg-gray-50'}`}>{s.label}</button>
          ))}
        </div>
        {canAdd(PERMISSIONS.ADD_MCPD) && (
        <button onClick={() => { setEditTraining(null); setShowForm(true); }}
          className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition flex items-center gap-2 self-start">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Create Training
        </button>
        )}
      </div>

      <div className="mb-5">
        <input type="text" placeholder="Search trainings..." value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
      </div>

      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B5D1E] mx-auto"></div></div>
      ) : trainings.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
          <p className="text-gray-500">No trainings found. Create your first bootcamp training!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {trainings.filter(t => {
            if (!search) return true;
            const s = search.toLowerCase();
            return (t.title || '').toLowerCase().includes(s) ||
              (t.category_display || '').toLowerCase().includes(s) ||
              (t.instructor_name || '').toLowerCase().includes(s);
          }).map((t, idx) => (
            <div key={t.id} className="bg-white rounded-xl shadow-sm border hover:shadow-md transition overflow-hidden relative">
              <div className={`h-2 ${
                t.category === 'surveying' ? 'bg-blue-500' :
                t.category === 'gis' ? 'bg-teal-500' :
                t.category === 'law' ? 'bg-amber-500' :
                t.category === 'ethics' ? 'bg-purple-500' :
                t.category === 'technology' ? 'bg-indigo-500' :
                t.category === 'field' ? 'bg-green-500' :
                t.category === 'management' ? 'bg-orange-500' : 'bg-gray-400'
              }`} />
              <div className="p-5">
                <span className="absolute top-4 right-4 bg-[#0B5D1E] text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center">{idx + 1}</span>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-900 truncate">{t.title}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{t.category_display}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ml-2 whitespace-nowrap ${STATUS_COLORS[t.status] || 'bg-gray-100 text-gray-600'}`}>
                    {t.status_display}
                  </span>
                </div>

                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    <span>{t.date_from} — {t.date_to}</span>
                  </div>
                  {t.instructor_name && (
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                      <span>{t.instructor_name}</span>
                    </div>
                  )}
                  {(t.venue || t.is_virtual) && (
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      <span>{t.is_virtual ? 'Virtual / Online' : t.venue}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 mb-4">
                  <span className={`text-xs px-2 py-0.5 rounded font-semibold ${LEVEL_COLORS[t.level] || ''}`}>{t.level_display}</span>
                  <span className="text-xs text-[#0B5D1E] font-bold">{t.cpd_points} CPD pts</span>
                  {t.max_participants ? (
                    <span className="text-xs text-gray-500">{t.enrolled_count}/{t.max_participants} seats</span>
                  ) : (
                    <span className="text-xs text-gray-500">{t.enrolled_count} enrolled</span>
                  )}
                </div>

                {Number(t.fee) > 0 && (
                  <p className="text-sm font-bold text-gray-800 mb-3">Fee: ₦{Number(t.fee).toLocaleString()}</p>
                )}

                <div className="flex gap-2 pt-3 border-t">
                  <button onClick={() => setViewTraining(t)}
                    className="flex-1 text-xs bg-[#0B5D1E] text-white px-3 py-2 rounded-lg font-semibold hover:bg-[#062E10] transition">
                    View Details
                  </button>
                  {canEdit(PERMISSIONS.EDIT_MCPD) && (
                  <button onClick={() => { setEditTraining(t); setShowForm(true); }}
                    className="text-xs bg-gray-100 text-gray-700 px-3 py-2 rounded-lg font-semibold hover:bg-gray-200 transition">
                    Edit
                  </button>
                  )}
                  {canDelete(PERMISSIONS.DELETE_MCPD) && (
                  <button onClick={() => handleDelete(t.id)}
                    className="text-xs bg-red-50 text-red-600 px-3 py-2 rounded-lg font-semibold hover:bg-red-100 transition">
                    Delete
                  </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Training Form (Create / Edit) ───

function TrainingForm({ training, onBack }) {
  const isEdit = !!training;
  const [form, setForm] = useState({
    title: training?.title || '',
    description: training?.description || '',
    category: training?.category || 'other',
    level: training?.level || 'beginner',
    instructor_name: training?.instructor_name || '',
    instructor_bio: training?.instructor_bio || '',
    venue: training?.venue || '',
    is_virtual: training?.is_virtual || false,
    meeting_link: training?.meeting_link || '',
    date_from: training?.date_from || '',
    date_to: training?.date_to || '',
    time_start: training?.time_start || '',
    time_end: training?.time_end || '',
    cpd_points: training?.cpd_points || 0,
    max_participants: training?.max_participants || '',
    fee: training?.fee || '0.00',
    status: training?.status || 'draft',
  });
  const [trainingFile, setTrainingFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const ACCEPTED_FILE_TYPES = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.mp4,.avi,.mov,.mkv,.webm';

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([key, val]) => {
        if (key === 'max_participants' && !val) return;
        if (key === 'time_start' && !val) return;
        if (key === 'time_end' && !val) return;
        fd.append(key, val);
      });
      if (trainingFile) fd.append('training_file', trainingFile);

      const config = { headers: { 'Content-Type': 'multipart/form-data' } };

      if (isEdit) {
        await api.put(`/admin/mcpd/trainings/${training.id}/`, fd, config);
        toast.success('Training updated!');
      } else {
        await api.post('/admin/mcpd/trainings/', fd, config);
        toast.success('Training created!');
      }
      onBack();
    } catch (err) {
      const detail = err.response?.data;
      if (detail && typeof detail === 'object') {
        const msg = Object.values(detail).flat().join(', ');
        toast.error(msg || 'Failed to save.');
      } else {
        toast.error('Failed to save training.');
      }
    } finally { setSaving(false); }
  };

  return (
    <div>
      <button onClick={onBack} className="text-sm text-[#0B5D1E] font-semibold hover:underline mb-4 flex items-center gap-1">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        Back to Trainings
      </button>

      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-6">{isEdit ? 'Edit Training' : 'Create New Training'}</h2>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <FormInput label="Training Title *" required value={form.title} onChange={e => handleChange('title', e.target.value)} />
            </div>
            <FormSelect label="Category" options={CATEGORY_OPTIONS} value={form.category} onChange={e => handleChange('category', e.target.value)} />
            <FormSelect label="Level" options={LEVEL_OPTIONS} value={form.level} onChange={e => handleChange('level', e.target.value)} />
            <FormInput label="Instructor Name" value={form.instructor_name} onChange={e => handleChange('instructor_name', e.target.value)} />
            <div>
              <FormSelect label="Status" options={STATUS_OPTIONS} value={form.status} onChange={e => handleChange('status', e.target.value)} />
              {form.status === 'draft' && (
                <p className="text-xs text-amber-600 mt-1 font-medium">⚠ Draft trainings are not visible to members. Set to "Upcoming" or "Ongoing" to publish.</p>
              )}
            </div>
            <FormInput label="Venue" value={form.venue} onChange={e => handleChange('venue', e.target.value)} />
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={form.is_virtual} onChange={e => handleChange('is_virtual', e.target.checked)}
                  className="w-4 h-4 text-[#0B5D1E] rounded focus:ring-[#0B5D1E]" />
                Virtual / Online
              </label>
            </div>
            {form.is_virtual && (
              <div className="md:col-span-2">
                <FormInput label="Meeting Link" type="url" value={form.meeting_link} onChange={e => handleChange('meeting_link', e.target.value)} />
              </div>
            )}
            <FormInput label="Start Date *" type="date" required value={form.date_from} onChange={e => handleChange('date_from', e.target.value)} />
            <FormInput label="End Date *" type="date" required value={form.date_to} onChange={e => handleChange('date_to', e.target.value)} />
            <FormInput label="Start Time" type="time" value={form.time_start} onChange={e => handleChange('time_start', e.target.value)} />
            <FormInput label="End Time" type="time" value={form.time_end} onChange={e => handleChange('time_end', e.target.value)} />
            <FormInput label="CPD Points *" type="number" min="0" required value={form.cpd_points} onChange={e => handleChange('cpd_points', parseInt(e.target.value) || 0)} />
            <FormInput label="Max Participants (blank = unlimited)" type="number" min="1" value={form.max_participants} onChange={e => handleChange('max_participants', e.target.value)} />
            <FormInput label="Fee (₦)" type="number" min="0" step="0.01" value={form.fee} onChange={e => handleChange('fee', e.target.value)} />
          </div>

          {/* Training File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Training File (PDF, Word, Image, or Video)</label>
            <input type="file" accept={ACCEPTED_FILE_TYPES} onChange={e => setTrainingFile(e.target.files[0])}
              className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#0B5D1E] file:text-white hover:file:bg-[#062E10] file:cursor-pointer" />
            <p className="text-xs text-gray-400 mt-1">Accepted: PDF, DOC, DOCX, JPEG, PNG, MP4, AVI, MOV, MKV, WEBM</p>
            {isEdit && training?.training_file && !trainingFile && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs text-gray-500">Current file:</span>
                <a href={training.training_file} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-[#0B5D1E] font-semibold hover:underline flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  View File
                </a>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
            <textarea rows={4} value={form.description} onChange={e => handleChange('description', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Instructor Bio</label>
            <textarea rows={3} value={form.instructor_bio} onChange={e => handleChange('instructor_bio', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <button type="submit" disabled={saving}
              className="bg-[#0B5D1E] text-white px-8 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition disabled:opacity-50">
              {saving ? 'Saving...' : (isEdit ? 'Update Training' : 'Create Training')}
            </button>
            <button type="button" onClick={onBack}
              className="bg-gray-100 text-gray-700 px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-200 transition">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ─── Training Detail View (Modules, Resources, Enrollments) ───

function TrainingDetailView({ training: initialTraining, onBack }) {
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('modules');

  const fetchTraining = useCallback(() => {
    setLoading(true);
    api.get(`/admin/mcpd/trainings/${initialTraining.id}/`)
      .then(r => setTraining(r.data))
      .catch(() => toast.error('Failed to load training.'))
      .finally(() => setLoading(false));
  }, [initialTraining.id]);

  useEffect(() => { fetchTraining(); }, [fetchTraining]);

  if (loading || !training) {
    return <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B5D1E] mx-auto"></div></div>;
  }

  return (
    <div>
      <button onClick={onBack} className="text-sm text-[#0B5D1E] font-semibold hover:underline mb-4 flex items-center gap-1">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        Back to Trainings
      </button>

      {/* Training Header */}
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h2 className="text-xl font-bold text-gray-900">{training.title}</h2>
              <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${STATUS_COLORS[training.status]}`}>{training.status_display}</span>
              <span className={`text-xs px-2.5 py-0.5 rounded font-semibold ${LEVEL_COLORS[training.level]}`}>{training.level_display}</span>
            </div>
            <p className="text-sm text-gray-500">{training.category_display}</p>
            {training.description && <p className="text-sm text-gray-600 mt-2">{training.description}</p>}
            {training.training_file && (
              <div className="mt-3">
                <a href={training.training_file} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm bg-[#0B5D1E] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#062E10] transition">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  View Training File
                </a>
              </div>
            )}
          </div>
          <div className="text-right space-y-1 flex-shrink-0">
            <p className="text-2xl font-bold text-[#0B5D1E]">{training.cpd_points} CPD</p>
            <p className="text-sm text-gray-500">{training.date_from} — {training.date_to}</p>
            {training.instructor_name && <p className="text-sm text-gray-600">Instructor: <strong>{training.instructor_name}</strong></p>}
            <p className="text-sm text-gray-500">
              {training.enrolled_count}{training.max_participants ? `/${training.max_participants}` : ''} enrolled
            </p>
          </div>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-fit">
        {['modules', 'resources', 'enrollments'].map(s => (
          <button key={s} onClick={() => setActiveSection(s)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition capitalize ${
              activeSection === s ? 'bg-white text-[#0B5D1E] shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>
            {s} {s === 'modules' ? `(${training.modules?.length || 0})` : s === 'resources' ? `(${training.resources?.length || 0})` : ''}
          </button>
        ))}
      </div>

      {activeSection === 'modules' && <ModulesSection training={training} onRefresh={fetchTraining} />}
      {activeSection === 'resources' && <ResourcesSection training={training} onRefresh={fetchTraining} />}
      {activeSection === 'enrollments' && <TrainingEnrollmentsSection training={training} onRefresh={fetchTraining} />}
    </div>
  );
}


// ─── Modules Section ───

function ModulesSection({ training, onRefresh }) {
  const { canEdit, canAdd, canDelete } = usePermissions();
  const [showForm, setShowForm] = useState(false);
  const [editModule, setEditModule] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', order: 0, duration_minutes: 60, date: '', time_start: '', time_end: '' });
  const [saving, setSaving] = useState(false);

  const openForm = (mod = null) => {
    if (mod) {
      setEditModule(mod);
      setForm({
        title: mod.title, description: mod.description || '', order: mod.order,
        duration_minutes: mod.duration_minutes, date: mod.date || '', time_start: mod.time_start || '', time_end: mod.time_end || '',
      });
    } else {
      setEditModule(null);
      setForm({ title: '', description: '', order: (training.modules?.length || 0) + 1, duration_minutes: 60, date: '', time_start: '', time_end: '' });
    }
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.date) payload.date = null;
      if (!payload.time_start) payload.time_start = null;
      if (!payload.time_end) payload.time_end = null;

      if (editModule) {
        await api.put(`/admin/mcpd/modules/${editModule.id}/`, { ...payload, training: training.id });
        toast.success('Module updated!');
      } else {
        await api.post(`/admin/mcpd/trainings/${training.id}/modules/`, payload);
        toast.success('Module added!');
      }
      setShowForm(false);
      onRefresh();
    } catch { toast.error('Failed to save module.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this module?')) return;
    try {
      await api.delete(`/admin/mcpd/modules/${id}/`);
      toast.success('Module deleted.');
      onRefresh();
    } catch { toast.error('Failed to delete.'); }
  };

  const handleToggleComplete = async (mod) => {
    try {
      await api.put(`/admin/mcpd/modules/${mod.id}/`, {
        title: mod.title, description: mod.description || '', order: mod.order,
        duration_minutes: mod.duration_minutes, date: mod.date, time_start: mod.time_start,
        time_end: mod.time_end, training: training.id, is_completed: !mod.is_completed,
      });
      onRefresh();
    } catch { toast.error('Failed to update.'); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-md font-bold text-gray-800">Training Modules / Sessions</h3>
        {canAdd(PERMISSIONS.ADD_MCPD) && (
        <button onClick={() => openForm()}
          className="bg-[#0B5D1E] text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-[#062E10] transition flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Add Module
        </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSave} className="bg-gray-50 rounded-xl border p-5 mb-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <FormInput label="Module Title *" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
            </div>
            <FormInput label="Order" type="number" min="0" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })} />
            <FormInput label="Duration (minutes)" type="number" min="1" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })} />
            <FormInput label="Date" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
            <FormInput label="Start Time" type="time" value={form.time_start} onChange={e => setForm({ ...form, time_start: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
            <textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={saving}
              className="bg-[#0B5D1E] text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition disabled:opacity-50">
              {saving ? 'Saving...' : (editModule ? 'Update Module' : 'Add Module')}
            </button>
            <button type="button" onClick={() => setShowForm(false)}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold">Cancel</button>
          </div>
        </form>
      )}

      {(!training.modules || training.modules.length === 0) ? (
        <div className="bg-white rounded-xl border p-8 text-center text-gray-400 text-sm">No modules yet. Add your first module above.</div>
      ) : (
        <div className="space-y-3">
          {training.modules.map((mod) => (
            <div key={mod.id} className={`bg-white rounded-xl border p-4 flex items-start gap-4 ${mod.is_completed ? 'border-green-200 bg-green-50' : ''}`}>
              {canEdit(PERMISSIONS.EDIT_MCPD) && (
              <button onClick={() => handleToggleComplete(mod)}
                className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition ${
                  mod.is_completed ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 hover:border-[#0B5D1E]'
                }`}>
                {mod.is_completed && <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
              </button>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono">#{mod.order}</span>
                  <h4 className={`font-semibold text-gray-900 ${mod.is_completed ? 'line-through text-gray-500' : ''}`}>{mod.title}</h4>
                </div>
                {mod.description && <p className="text-sm text-gray-500">{mod.description}</p>}
                <div className="flex gap-3 text-xs text-gray-400 mt-1">
                  <span>{mod.duration_minutes} min</span>
                  {mod.date && <span>{mod.date}</span>}
                  {mod.time_start && <span>{mod.time_start}</span>}
                </div>
              </div>
              <div className="flex gap-1">
                {canEdit(PERMISSIONS.EDIT_MCPD) && (
                <button onClick={() => openForm(mod)} className="text-xs text-gray-500 hover:text-[#0B5D1E] p-1" title="Edit">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                </button>
                )}
                {canDelete(PERMISSIONS.DELETE_MCPD) && (
                <button onClick={() => handleDelete(mod.id)} className="text-xs text-gray-500 hover:text-red-600 p-1" title="Delete">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Resources Section ───

function ResourcesSection({ training, onRefresh }) {
  const { canEdit, canAdd, canDelete } = usePermissions();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', resource_type: 'document', external_url: '', order: 0, module: '' });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const RESOURCE_ICONS = { document: '📄', video: '🎥', link: '🔗', slide: '📊' };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('description', form.description);
      fd.append('resource_type', form.resource_type);
      fd.append('external_url', form.external_url);
      fd.append('order', form.order);
      if (form.module) fd.append('module', form.module);
      if (file) fd.append('file', file);

      await api.post(`/admin/mcpd/trainings/${training.id}/resources/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Resource added!');
      setShowForm(false);
      setForm({ title: '', description: '', resource_type: 'document', external_url: '', order: 0, module: '' });
      setFile(null);
      onRefresh();
    } catch { toast.error('Failed to save resource.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this resource?')) return;
    try {
      await api.delete(`/admin/mcpd/resources/${id}/`);
      toast.success('Resource deleted.');
      onRefresh();
    } catch { toast.error('Failed to delete.'); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-md font-bold text-gray-800">Training Resources & Materials</h3>
        {canAdd(PERMISSIONS.ADD_MCPD) && (
        <button onClick={() => setShowForm(!showForm)}
          className="bg-[#0B5D1E] text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-[#062E10] transition flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Add Resource
        </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSave} className="bg-gray-50 rounded-xl border p-5 mb-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="Title *" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
            <FormSelect label="Type" options={[
              { value: 'document', label: 'Document' },
              { value: 'video', label: 'Video' },
              { value: 'link', label: 'External Link' },
              { value: 'slide', label: 'Slide/Presentation' },
            ]} value={form.resource_type} onChange={e => setForm({ ...form, resource_type: e.target.value })} />
            {training.modules?.length > 0 && (
              <FormSelect label="Linked Module (optional)"
                options={[{ value: '', label: '— None —' }, ...training.modules.map(m => ({ value: m.id, label: `#${m.order} ${m.title}` }))]}
                value={form.module} onChange={e => setForm({ ...form, module: e.target.value })} />
            )}
            <FormInput label="Order" type="number" min="0" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })} />
          </div>
          {(form.resource_type === 'link' || form.resource_type === 'video') && (
            <FormInput label="URL" type="url" value={form.external_url} onChange={e => setForm({ ...form, external_url: e.target.value })} />
          )}
          {(form.resource_type === 'document' || form.resource_type === 'slide') && (
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Upload File</label>
              <input type="file" onChange={e => setFile(e.target.files[0])} className="text-sm" />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
            <textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={saving}
              className="bg-[#0B5D1E] text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
              {saving ? 'Saving...' : 'Add Resource'}
            </button>
            <button type="button" onClick={() => setShowForm(false)}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold">Cancel</button>
          </div>
        </form>
      )}

      {(!training.resources || training.resources.length === 0) ? (
        <div className="bg-white rounded-xl border p-8 text-center text-gray-400 text-sm">No resources yet.</div>
      ) : (
        <div className="space-y-2">
          {training.resources.map(res => (
            <div key={res.id} className="bg-white rounded-xl border p-4 flex items-center gap-4">
              <span className="text-2xl">{RESOURCE_ICONS[res.resource_type] || '📁'}</span>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 text-sm">{res.title}</h4>
                <p className="text-xs text-gray-500 capitalize">{res.resource_type_display}</p>
                {res.description && <p className="text-xs text-gray-400 mt-0.5">{res.description}</p>}
              </div>
              {(res.file || res.external_url) && (
                <a href={res.file || res.external_url} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-[#0B5D1E] font-semibold hover:underline">Open</a>
              )}
              {canDelete(PERMISSIONS.DELETE_MCPD) && (
              <button onClick={() => handleDelete(res.id)} className="text-gray-400 hover:text-red-600">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Training Enrollments Section ───

function TrainingEnrollmentsSection({ training, onRefresh }) {
  const { canEdit } = usePermissions();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [showSingleForm, setShowSingleForm] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [singleEnrolling, setSingleEnrolling] = useState(false);

  const fetchEnrollments = useCallback(() => {
    setLoading(true);
    api.get(`/admin/mcpd/trainings/${training.id}/enrollments/`)
      .then(r => setEnrollments(r.data.results || r.data))
      .catch(() => toast.error('Failed to load enrollments.'))
      .finally(() => setLoading(false));
  }, [training.id]);

  useEffect(() => { fetchEnrollments(); }, [fetchEnrollments]);

  // Search members for single enrollment
  useEffect(() => {
    if (memberSearch.length < 2) { setMemberResults([]); return; }
    const timer = setTimeout(() => {
      api.get(`/accounts/admin/members/?search=${encodeURIComponent(memberSearch)}&page_size=10`)
        .then(r => setMemberResults(r.data.results || r.data))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearch]);

  const handleAction = async (enrollId, action, extraData = {}) => {
    try {
      await api.post(`/admin/mcpd/enrollments/${enrollId}/action/`, { action, ...extraData });
      toast.success(`Action successful.`);
      fetchEnrollments();
      onRefresh();
    } catch { toast.error('Action failed.'); }
  };

  const handleBulkEnroll = async () => {
    if (!window.confirm('Enroll ALL active members into this training?')) return;
    setEnrolling(true);
    try {
      const res = await api.post(`/admin/mcpd/trainings/${training.id}/bulk-enroll/`, {});
      toast.success(res.data.detail);
      fetchEnrollments();
      onRefresh();
    } catch { toast.error('Bulk enrollment failed.'); }
    finally { setEnrolling(false); }
  };

  const handleSingleEnroll = async () => {
    if (!selectedMember) return;
    setSingleEnrolling(true);
    try {
      await api.post(`/admin/mcpd/trainings/${training.id}/single-enroll/`, { member_id: selectedMember.id });
      toast.success(`${selectedMember.full_name || selectedMember.user_full_name} enrolled successfully`);
      setSelectedMember(null);
      setMemberSearch('');
      setShowSingleForm(false);
      fetchEnrollments();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Enrollment failed');
    }
    setSingleEnrolling(false);
  };

  const ENROLLMENT_COLORS = {
    enrolled: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    dropped: 'bg-red-100 text-red-700',
    waitlisted: 'bg-yellow-100 text-yellow-700',
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-md font-bold text-gray-800">Enrolled Members ({enrollments.length})</h3>
        <div className="flex gap-2">
          {canEdit(PERMISSIONS.EDIT_MCPD) && (
          <button onClick={() => setShowSingleForm(!showSingleForm)}
            className="bg-[#0B5D1E] text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-[#062E10] transition">
            {showSingleForm ? 'Cancel' : '+ Enroll Member'}
          </button>
          )}
          {canEdit(PERMISSIONS.EDIT_MCPD) && (
          <button onClick={handleBulkEnroll} disabled={enrolling}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-purple-700 transition disabled:opacity-50">
            {enrolling ? 'Enrolling...' : 'Bulk Enroll Active Members'}
          </button>
          )}
        </div>
      </div>

      {/* Single Enrollment Form */}
      {showSingleForm && (
        <div className="bg-gray-50 rounded-xl border p-5 mb-5">
          <h4 className="font-semibold text-gray-700 mb-3">Enroll Single Member</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-600 mb-1">Search Member</label>
              <input type="text" value={memberSearch} onChange={e => { setMemberSearch(e.target.value); setSelectedMember(null); }}
                placeholder="Type name or membership number..."
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
              {memberResults.length > 0 && !selectedMember && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto">
                  {memberResults.map(m => (
                    <button key={m.id} type="button"
                      onClick={() => {
                        setSelectedMember(m);
                        setMemberSearch(`${m.full_name || m.user_full_name} (${m.membership_number || ''})`);
                        setMemberResults([]);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm">
                      <span className="font-medium">{m.full_name || m.user_full_name}</span>
                      <span className="text-gray-400 ml-2">{m.membership_number}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-end">
              <button onClick={handleSingleEnroll}
                disabled={!selectedMember || singleEnrolling}
                className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition disabled:opacity-50 w-full">
                {singleEnrolling ? 'Enrolling...' : 'Enroll Member'}
              </button>
            </div>
          </div>
          {selectedMember && (
            <div className="mt-3 p-3 bg-green-50 rounded-lg text-sm text-green-800">
              Selected: <strong>{selectedMember.full_name || selectedMember.user_full_name}</strong> — {selectedMember.membership_number}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B5D1E] mx-auto"></div></div>
      ) : enrollments.length === 0 ? (
        <div className="bg-white rounded-xl border p-8 text-center text-gray-400 text-sm">No enrollments yet.</div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">S/N</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Member</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">ID</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Attendance</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">CPD Awarded</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Enrolled</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {enrollments.map((en, idx) => (
                <tr key={en.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-500 font-medium">{idx + 1}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{en.member_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 font-mono">{en.membership_number}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${ENROLLMENT_COLORS[en.status]}`}>{en.status_display}</span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {en.attendance_marked ? (
                      <span className="text-green-600 font-semibold">Present</span>
                    ) : (
                      <button onClick={() => handleAction(en.id, 'mark_attendance')}
                        className="text-xs text-blue-600 hover:underline font-semibold">Mark Present</button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-[#0B5D1E]">{en.cpd_awarded || '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{new Date(en.enrolled_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      {en.status === 'enrolled' && (
                        <>
                          <button onClick={() => handleAction(en.id, 'complete', { cpd_points: training.cpd_points })}
                            className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700">Complete</button>
                          <button onClick={() => handleAction(en.id, 'drop')}
                            className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded hover:bg-red-100">Drop</button>
                        </>
                      )}
                      {en.status === 'completed' && !en.certificate_issued && (
                        <button onClick={() => handleAction(en.id, 'issue_certificate')}
                          className="text-xs bg-purple-600 text-white px-2 py-1 rounded hover:bg-purple-700">Issue Cert</button>
                      )}
                      {en.certificate_issued && <span className="text-xs text-purple-600 font-semibold">Cert Issued</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// ═══ ENROLLMENTS TAB (All enrollments across trainings) ═══
// ══════════════════════════════════════════════════════════════

function EnrollmentsTab() {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const fetchEnrollments = useCallback(() => {
    setLoading(true);
    const params = statusFilter ? `?status=${statusFilter}` : '';
    api.get(`/admin/mcpd/enrollments/${params}`)
      .then(r => setEnrollments(r.data.results || r.data))
      .catch(() => toast.error('Failed to load enrollments.'))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { fetchEnrollments(); }, [fetchEnrollments]);

  const handleAction = async (enrollId, action, extraData = {}) => {
    try {
      await api.post(`/admin/mcpd/enrollments/${enrollId}/action/`, { action, ...extraData });
      toast.success('Action successful.');
      fetchEnrollments();
    } catch { toast.error('Action failed.'); }
  };

  const ENROLLMENT_COLORS = {
    enrolled: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    dropped: 'bg-red-100 text-red-700',
    waitlisted: 'bg-yellow-100 text-yellow-700',
  };

  return (
    <div>
      <div className="flex gap-2 mb-6 flex-wrap">
        <button onClick={() => setStatusFilter('')}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${!statusFilter ? 'bg-[#0B5D1E] text-white' : 'bg-white border text-gray-600 hover:bg-gray-50'}`}>All</button>
        {[{ v: 'enrolled', l: 'Enrolled' }, { v: 'completed', l: 'Completed' }, { v: 'dropped', l: 'Dropped' }].map(f => (
          <button key={f.v} onClick={() => setStatusFilter(f.v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${statusFilter === f.v ? 'bg-[#0B5D1E] text-white' : 'bg-white border text-gray-600 hover:bg-gray-50'}`}>{f.l}</button>
        ))}
      </div>

      <div className="mb-5">
        <input type="text" placeholder="Search enrollments..." value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
      </div>

      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B5D1E] mx-auto"></div></div>
      ) : enrollments.length === 0 ? (
        <div className="bg-white rounded-xl border p-8 text-center text-gray-400 text-sm">No enrollments found.</div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">S/N</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Member</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Training</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Attendance</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">CPD</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Certificate</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Enrolled</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {enrollments.filter(en => {
                if (!search) return true;
                const s = search.toLowerCase();
                return (en.member_name || '').toLowerCase().includes(s) ||
                  (en.training_title || '').toLowerCase().includes(s) ||
                  (en.membership_number || '').toLowerCase().includes(s);
              }).map((en, idx) => (
                <tr key={en.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-500 font-medium">{idx + 1}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{en.member_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{en.training_title}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${ENROLLMENT_COLORS[en.status]}`}>{en.status_display}</span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {en.attendance_marked ? <span className="text-green-600 font-semibold">Present</span> : (
                      <button onClick={() => handleAction(en.id, 'mark_attendance')} className="text-xs text-blue-600 hover:underline font-semibold">Mark</button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-[#0B5D1E]">{en.cpd_awarded || '—'}</td>
                  <td className="px-4 py-3 text-sm">
                    {en.certificate_issued ? <span className="text-purple-600 font-semibold">Issued</span> : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{new Date(en.enrolled_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      {en.status === 'enrolled' && (
                        <>
                          <button onClick={() => handleAction(en.id, 'complete')}
                            className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700">Complete</button>
                          <button onClick={() => handleAction(en.id, 'drop')}
                            className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded hover:bg-red-100">Drop</button>
                        </>
                      )}
                      {en.status === 'completed' && !en.certificate_issued && (
                        <button onClick={() => handleAction(en.id, 'issue_certificate')}
                          className="text-xs bg-purple-600 text-white px-2 py-1 rounded hover:bg-purple-700">Issue Cert</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// ═══ MCPD RECORDS TAB (Existing review system) ═══
// ══════════════════════════════════════════════════════════════

function MCPDRecordsTab() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');

  const fetchRecords = useCallback(() => {
    setLoading(true);
    const params = filter ? `?status=${filter}` : '';
    api.get(`/admin/mcpd/${params}`)
      .then(r => setRecords(r.data.results || r.data))
      .catch(() => toast.error('Failed to load records.'))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const handleReview = async (id, action) => {
    let comment = '';
    if (action === 'reject') {
      comment = prompt('Reason for rejection:');
      if (comment === null) return;
    }
    try {
      await api.post(`/admin/mcpd/${id}/review/`, { action, comment });
      toast.success(`Record ${action}d!`);
      fetchRecords();
    } catch { toast.error('Failed to review.'); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-6 flex-wrap">
        {['pending', 'approved', 'rejected', ''].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              filter === f ? 'bg-[#0B5D1E] text-white' : 'bg-white border text-gray-600 hover:bg-gray-50'
            }`}>{f || 'All'}</button>
        ))}
      </div>

      <div className="mb-5">
        <input type="text" placeholder="Search records..." value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
      </div>

      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B5D1E] mx-auto"></div></div>
        ) : records.filter(r => {
            if (!search) return true;
            const s = search.toLowerCase();
            return (r.title || '').toLowerCase().includes(s) ||
              (r.member_name || '').toLowerCase().includes(s) ||
              (r.organizer || '').toLowerCase().includes(s) ||
              (r.activity_type || '').toLowerCase().includes(s);
          }).length === 0 ? (
          <div className="bg-white rounded-xl border p-8 text-center text-gray-400 text-sm">No records found.</div>
        ) : records.filter(r => {
            if (!search) return true;
            const s = search.toLowerCase();
            return (r.title || '').toLowerCase().includes(s) ||
              (r.member_name || '').toLowerCase().includes(s) ||
              (r.organizer || '').toLowerCase().includes(s) ||
              (r.activity_type || '').toLowerCase().includes(s);
          }).map((r, idx) => (
          <div key={r.id} className="bg-white rounded-xl shadow-sm border p-5">
            <div className="flex justify-between items-start">
              <div className="flex gap-3 flex-1">
                <span className="bg-[#0B5D1E] text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1">{idx + 1}</span>
                <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-semibold text-gray-900">{r.title}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                    r.status === 'approved' ? 'bg-green-100 text-green-700' :
                    r.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>{r.status}</span>
                </div>
                <p className="text-sm text-gray-500">
                  <strong>{r.member_name}</strong> | {r.activity_type?.replace('_', ' ')} | {r.organizer}
                </p>
                <p className="text-sm text-gray-500">{r.date_from} to {r.date_to} | <span className="font-semibold text-[#0B5D1E]">{r.cpd_points} CPD Points</span></p>
                {r.description && <p className="text-sm text-gray-600 mt-2">{r.description}</p>}
                {r.reviewer_comment && <p className="text-sm text-blue-600 mt-1 italic">Review: {r.reviewer_comment}</p>}
              </div>
              </div>
              {r.status === 'pending' && (
                <div className="flex gap-2 ml-4">
                  <button onClick={() => handleReview(r.id, 'approve')}
                    className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 transition font-semibold">
                    Approve
                  </button>
                  <button onClick={() => handleReview(r.id, 'reject')}
                    className="text-xs bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 transition font-semibold">
                    Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
